/**
 * Size-gated auto-re-attach of small uploaded files (PLT-5558).
 *
 * Uploaded files are never stored server-side — each is deleted right after the
 * chat turn that carried it (see `chat/anthropic-files.ts`, `TurnFileLifecycle`).
 * So when the agent asks the user to fix a formatting issue in a holdings CSV,
 * a follow-up turn would otherwise force a full re-upload of the same file. To
 * smooth that iteration loop we keep the file's already-read base64 in
 * thread-scoped browser session state and auto-re-attach it on later turns that
 * carry no new upload. Nothing is persisted server-side, so this stays a pure
 * frontend change with no data-residency/infosec impact.
 *
 * This is gated on file SIZE. Re-attaching re-uploads the bytes and re-tokenizes
 * the whole document into the model context at full (uncached) input cost on
 * *every* turn — the chat pipeline has no per-file dedup/caching. For small
 * files that is negligible; for large holdings CSVs (thousands of rows) it would
 * add material per-turn cost and latency that grows with the conversation
 * length, so files at or above the cutoff are deliberately NOT remembered
 * (today's behaviour: the user re-uploads if needed). The cutoff is in raw
 * (decoded) bytes; ~100 KB ≈ a few hundred holdings ≈ 15-30k tokens.
 *
 * Kept as a tiny standalone module (no React / browser-storage imports) so the
 * logic can be unit-tested in isolation and imported by both the chat adapter
 * and the shared reset path without pulling in the wider chat module.
 */

export const MAX_AUTO_REATTACH_BYTES = 100 * 1024;

export type ChatAttachmentPayload = {
  data: string;
  filename: string;
  mimeType: string;
  /** Set on replayed (auto-re-attached) files so the server can tell them from a
   * fresh upload — see {@link selectOutgoingAttachments} and the holdings-staging
   * intake. Absent on fresh uploads. */
  reattached?: boolean;
};

/**
 * Most recent small attachment(s) uploaded in the current chat thread, kept so
 * follow-up turns can auto-re-attach them. Scoped by `threadId` so a `New chat`
 * (which rotates the thread id) starts clean even before the explicit clear in
 * `resetChatLoggingState`.
 */
export const rememberedSmallAttachments: {
  threadId: string | null;
  value: ChatAttachmentPayload[];
} = { threadId: null, value: [] };

/** Clear the remembered auto-re-attach file(s). */
export function resetRememberedSmallAttachments(): void {
  rememberedSmallAttachments.threadId = null;
  rememberedSmallAttachments.value = [];
}

/**
 * Whether auto-re-attach is active for the current user (PLT-5607).
 *
 * The whole feature exists for the fund-upload iteration loop, so it is scoped
 * to callers holding the `USE_FUND_UPLOAD` entitlement — chat has no
 * per-conversation "this is a fund upload" signal, so the entitlement is the
 * meaningful gate. The chat runtime provider sets this from
 * `useHasResourcelessPrivilege(USE_FUND_UPLOAD)`. Defaults to `false` so that
 * before the privilege resolves — and for every user without it — the chat
 * keeps today's behaviour: this turn's uploads are sent as-is and nothing is
 * remembered or replayed. Held as module state (not React) because the chat
 * adapter runs outside the React tree, mirroring `composerState`.
 */
export const autoReattachEnabled = { value: false };

/** Set whether auto-re-attach is active (see {@link autoReattachEnabled}). */
export function setAutoReattachEnabled(enabled: boolean): void {
  autoReattachEnabled.value = enabled;
}

/** Approximate decoded byte length of a base64 string (padding ignored). */
export function base64ByteLength(base64: string): number {
  return Math.floor((base64.length * 3) / 4);
}

/**
 * Decide which attachments to send for the current turn, and update the
 * thread-scoped remembered-attachment state as a side effect.
 *
 * - When `enabled` is false (the caller lacks the `USE_FUND_UPLOAD`
 *   entitlement — PLT-5607, or the privilege has not resolved yet), this turn's
 *   uploads are sent as-is and NOTHING is remembered or replayed — the exact
 *   behaviour before PLT-5558. This is the gate that keeps auto-re-attach out of
 *   every non-fund upload chat (DMA exports, financial statements, …).
 * - If this turn carries fresh uploads, they are sent as-is and the small ones
 *   (below MAX_AUTO_REATTACH_BYTES) become the remembered set for this thread —
 *   a new upload always replaces whatever was remembered before.
 * - Otherwise, if the current thread has a remembered small file, it is
 *   replayed (tagged `reattached: true`) so the user doesn't have to re-upload
 *   to keep iterating. The tag lets the server distinguish this silent replay
 *   from a deliberate re-upload (PLT-5520 holdings-staging dedup).
 * - Otherwise nothing is attached (large files are never remembered, so they
 *   fall through here — today's behaviour, the user re-uploads if needed).
 */
export function selectOutgoingAttachments(
  uploadedThisTurn: ChatAttachmentPayload[],
  threadId: string,
  enabled: boolean,
): ChatAttachmentPayload[] {
  // PLT-5607: no remember/replay for callers without the fund-upload
  // entitlement. Return this turn's uploads untouched and leave the remembered
  // set alone.
  if (!enabled) return uploadedThisTurn;
  if (uploadedThisTurn.length > 0) {
    rememberedSmallAttachments.threadId = threadId;
    rememberedSmallAttachments.value = uploadedThisTurn.filter(a => base64ByteLength(a.data) < MAX_AUTO_REATTACH_BYTES);
    return uploadedThisTurn;
  }
  if (rememberedSmallAttachments.threadId === threadId && rememberedSmallAttachments.value.length > 0) {
    // Replay: tag each so the server treats it as an auto-re-attach, not a fresh
    // upload. A fresh upload (the branch above) is returned untagged.
    return rememberedSmallAttachments.value.map(a => ({ ...a, reattached: true }));
  }
  return [];
}
